import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type SpeedingEvent } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, type Column } from '@/components/ui';
import { IconBan } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader } from '@/components/admin/shared';

/** Speeding — the evidence log. When a customer reports a driver was speeding,
 *  ops open this and filter to the driver (or the trip) to see every time the
 *  driver exceeded the posted limit: when, where (road + GPS), how fast vs the
 *  limit, and for how long. safety-svc detects these live from the GPS stream
 *  (only on roads where the posted limit is known, and only sustained overage,
 *  so a single bad GPS reading can't create a false record) and writes them to
 *  the shared `speeding_events` table; this view reads them back. Read-only. */
export function Speeding() {
  const [driverId, setDriverId] = useState('');
  const [tripId, setTripId] = useState('');
  // Applied filters (only updated on submit, so typing doesn't refetch).
  const [applied, setApplied] = useState<{ driver_id?: number; trip_id?: number }>({});

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'speeding', applied],
    queryFn: ({ signal }) => adminApi.speeding({ ...applied, limit: 200 }, signal),
  });

  const events = data?.events ?? [];

  const apply = () => {
    const d = driverId.trim() === '' ? undefined : Number(driverId.trim());
    const t = tripId.trim() === '' ? undefined : Number(tripId.trim());
    setApplied({
      driver_id: Number.isFinite(d) ? d : undefined,
      trip_id: Number.isFinite(t) ? t : undefined,
    });
  };
  const clear = () => {
    setDriverId('');
    setTripId('');
    setApplied({});
  };

  const columns: Array<Column<SpeedingEvent>> = [
    {
      key: 'occurred_at',
      header: 'When',
      cell: (r) => <span className="metric text-xs text-slate-300">{fmtTime(r.occurred_at)}</span>,
    },
    { key: 'driver_id', header: 'Driver', cell: (r) => <span className="metric text-xs text-slate-300">#{r.driver_id || '—'}</span> },
    { key: 'trip_id', header: 'Trip', cell: (r) => <span className="metric text-xs text-slate-300">#{r.trip_id || '—'}</span> },
    { key: 'road', header: 'Road', cell: (r) => <span className="text-xs text-slate-200">{r.road || '—'}</span> },
    {
      key: 'speed',
      header: 'Speed vs limit',
      cell: (r) => (
        <span className="metric text-xs text-slate-200">
          {r.observed_kmh} <span className="text-muted">/ {r.limit_kmh} km/h</span>
        </span>
      ),
    },
    {
      key: 'over',
      header: 'Over',
      cell: (r) => (
        <Badge tone={r.over_kmh >= 20 ? 'danger' : 'warn'} dot>
          +{r.over_kmh} km/h
        </Badge>
      ),
    },
    { key: 'sustained_s', header: 'For', cell: (r) => <span className="text-xs text-slate-300">{r.sustained_s ? `${r.sustained_s}s` : '—'}</span> },
    {
      key: 'where',
      header: 'Map',
      align: 'right',
      cell: (r) => (
        <a
          href={`https://www.google.com/maps?q=${r.lat},${r.lng}`}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-brand-400 hover:underline"
        >
          view
        </a>
      ),
    },
  ];

  return (
    <AdminPage>
      <AdminPageHeader
        title="Speeding"
        subtitle="Recorded instances of a driver exceeding the posted speed limit — the proof to pull when a customer reports speeding. Filter by driver or trip."
      />

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[11px] text-muted">Driver ID</label>
            <input
              value={driverId}
              onChange={(e) => setDriverId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && apply()}
              placeholder="e.g. 1042"
              className="mt-1 input w-40"
            />
          </div>
          <div>
            <label className="block text-[11px] text-muted">Trip ID</label>
            <input
              value={tripId}
              onChange={(e) => setTripId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && apply()}
              placeholder="optional"
              className="mt-1 input w-40"
            />
          </div>
          <button type="button" onClick={apply} className="btn-primary">
            Search
          </button>
          {(applied.driver_id || applied.trip_id) ? (
            <button type="button" onClick={clear} className="btn-secondary">
              Clear
            </button>
          ) : null}
        </div>
      </Card>

      <div className="mt-4">
        <Card flush>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner label="Loading speeding events…" />
            </div>
          ) : isError ? (
            <EmptyState title="Couldn't load speeding events" description={(error as Error)?.message} />
          ) : (
            <DataTable<SpeedingEvent>
              columns={columns}
              rows={events}
              rowKey={(r) => String(r.id)}
              stickyHeader
              empty={
                <EmptyState
                  title="No speeding events"
                  description="Nothing recorded for this filter. Events appear here when a driver sustains a speed over the posted limit on a known-limit road."
                  icon={<IconBan className="h-6 w-6" />}
                />
              }
            />
          )}
        </Card>
      </div>
    </AdminPage>
  );
}

/** Local time formatter — defensive against a bad/empty timestamp. */
function fmtTime(s: string): string {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString();
}
